// Pista de auditoría. Sistemas → Gestión de Autenticación.
//
// Lo que esta pantalla existe para responder: «¿quién cambió esto, cuándo, y
// qué valor tenía antes?». Hasta el script 782 la base guardaba esa respuesta
// en once esquemas y en ninguno de los cuatro que se auditan de verdad —
// contabilidad, financiero, control bancario y autenticación—, y además no
// había ninguna pantalla que leyera ninguna de las quince.
//
// Dos pestañas porque son dos preguntas distintas del auditor: qué se tocó
// (cambios) y quién entró (accesos).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { History, KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const AUTH = { headers: { 'Content-Profile': 'autenticacion', 'Accept-Profile': 'autenticacion' } };
const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, AUTH)).data;

const field = 'flex h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface Cambio {
  esquema: string; tabla: string; registro_id: string | null; operacion: string;
  campos_cambiados: string[] | null;
  datos_antes: Record<string, unknown> | null;
  datos_despues: Record<string, unknown> | null;
  usuario: string | null; fecha: string; ip: string | null; user_agent: string | null;
}
interface Acceso {
  fecha: string; username: string | null; usuario: string | null;
  resultado: string; ip: string | null; user_agent: string | null;
}
interface NuncaEntro { username: string; perfil: string | null; nivel: number | null; creado: string }
interface Accesos {
  accesos: Acceso[]; nunca_entraron: NuncaEntro[];
  resumen: { entradas: number; fallidos: number; usuarios_distintos: number; ips_distintas: number };
}

const hace = (iso: string) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return i18n.t('admin:pista_hace_min', { n: Math.max(m, 0) });
  const h = Math.floor(m / 60);
  if (h < 24) return i18n.t('admin:pista_hace_h', { n: h });
  return i18n.t('admin:pista_hace_d', { n: Math.floor(h / 24) });
};

const Op = ({ o }: { o: string }) => {
  const { t } = useTranslation('admin');
  return (
  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
    o === 'DELETE' ? 'bg-red-500/10 text-red-600'
      : o === 'INSERT' ? 'bg-green-500/10 text-green-600'
      : 'bg-amber-500/10 text-amber-600'}`}>
    {o === 'DELETE' ? t('pista_borro') : o === 'INSERT' ? t('pista_creo') : t('pista_cambio')}
  </span>
  );
};

// Sólo se muestran los campos que cambiaron: volcar la fila entera convierte la
// pista en algo que nadie lee.
function Diff({ c }: { c: Cambio }) {
  const { t } = useTranslation('admin');
  const campos = c.campos_cambiados ?? [];
  if (c.operacion === 'DELETE') {
    return <span className="text-xs text-muted-foreground italic">{t('pista_reg_eliminado')}</span>;
  }
  if (c.operacion === 'INSERT') {
    return <span className="text-xs text-muted-foreground italic">{t('pista_reg_creado')}</span>;
  }
  return (
    <div className="space-y-0.5">
      {campos.slice(0, 6).map((k) => (
        <div key={k} className="text-xs">
          <span className="text-muted-foreground">{k}: </span>
          <span className="line-through opacity-60">{String(c.datos_antes?.[k] ?? '—').slice(0, 60)}</span>
          <span className="mx-1">→</span>
          <span className="font-medium">{String(c.datos_despues?.[k] ?? '—').slice(0, 60)}</span>
        </div>
      ))}
      {campos.length > 6 && <div className="text-xs text-muted-foreground">{t('pista_mas_campos', { n: campos.length - 6 })}</div>}
    </div>
  );
}

export function PistaAuditoria() {
  const { t } = useTranslation('admin');
  const [tab, setTab] = useState<'cambios' | 'accesos'>('cambios');
  const [esquema, setEsquema] = useState('');
  const [tabla, setTabla] = useState('');
  const [operacion, setOperacion] = useState('');
  const [dias, setDias] = useState(30);

  const desde = new Date(Date.now() - dias * 86400000).toISOString();

  const cambios = useQuery<Cambio[]>({
    queryKey: ['aud-cambios', esquema, tabla, operacion, dias],
    enabled: tab === 'cambios',
    queryFn: () => rpc<Cambio[]>('aud_consultar', {
      _desde: desde, _esquema: esquema || null, _tabla: tabla || null,
      _operacion: operacion || null, _limite: 300,
    }),
  });

  const accesos = useQuery<Accesos>({
    queryKey: ['aud-accesos', dias],
    enabled: tab === 'accesos',
    queryFn: () => rpc<Accesos>('aud_accesos', { _desde: desde, _limite: 300 }),
  });

  const filas = cambios.data ?? [];
  const acc = accesos.data;

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <History className="w-5 h-5" /> {t('pista_titulo')}
      </h1>
      <p className="text-sm text-muted-foreground">
        {t('pista_intro')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {([['cambios', t('pista_tab_cambios')], ['accesos', t('pista_tab_accesos')]] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1 rounded-full text-sm border ${
              tab === k ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
            {l}
          </button>
        ))}
        <label className="flex items-center gap-1.5 text-sm ml-2">
          <span className="text-muted-foreground">{t('pista_ultimos')}</span>
          <select className={`${field} w-24`} value={dias} onChange={(e) => setDias(Number(e.target.value))}>
            {[7, 30, 90, 365].map((d) => <option key={d} value={d}>{t('pista_dias', { d })}</option>)}
          </select>
        </label>
        {tab === 'cambios' && (
          <>
            <select className={`${field} w-44`} value={esquema} onChange={(e) => setEsquema(e.target.value)}>
              <option value="">{t('pista_todos_modulos')}</option>
              {['contabilidad', 'financiero', 'control_bancario', 'autenticacion', 'inventario',
                'nomina', 'pos', 'medical', 'activofijo'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input className={`${field} w-44`} placeholder={t('pista_tabla_ph')}
              value={tabla} onChange={(e) => setTabla(e.target.value.trim())} />
            <select className={`${field} w-36`} value={operacion} onChange={(e) => setOperacion(e.target.value)}>
              <option value="">{t('pista_toda_op')}</option>
              <option value="UPDATE">{t('pista_op_cambios')}</option>
              <option value="DELETE">{t('pista_op_borrados')}</option>
              <option value="INSERT">{t('pista_op_altas')}</option>
            </select>
          </>
        )}
        {(cambios.isFetching || accesos.isFetching) && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>

      {tab === 'cambios' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">
            {t('pista_movs', { n: filas.length })}{filas.length >= 300 && t('pista_limite')}
          </CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {filas.length === 0 && !cambios.isFetching && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t('pista_sin_cambios')}
              </p>
            )}
            {filas.length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">{t('pista_c_cuando')}</th><th className="py-2 pr-3">{t('pista_c_quien')}</th>
                  <th className="py-2 pr-3">{t('pista_c_donde')}</th><th className="py-2 pr-3">{t('pista_c_que')}</th>
                  <th className="py-2 pr-3">{t('pista_c_desde')}</th>
                </tr></thead>
                <tbody>
                  {filas.map((c, i) => (
                    <tr key={i} className="border-b last:border-0 align-top hover:bg-muted/40">
                      <td className="py-2 pr-3 whitespace-nowrap text-xs">
                        {new Date(c.fecha).toLocaleString(LOCALE)}
                        <div className="text-muted-foreground">{hace(c.fecha)}</div>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {c.usuario ?? <span className="text-muted-foreground italic">{t('pista_proceso')}</span>}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap text-xs">
                        <Op o={c.operacion} />
                        <div className="mt-0.5 font-mono">{c.esquema}.{c.tabla}</div>
                      </td>
                      <td className="py-2 pr-3 max-w-lg"><Diff c={c} /></td>
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs text-muted-foreground"
                        title={c.user_agent ?? ''}>{c.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'accesos' && (
        <>
          {acc && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { l: t('pista_k_entradas'), v: acc.resumen?.entradas ?? 0, c: '' },
                { l: t('pista_k_fallidos'), v: acc.resumen?.fallidos ?? 0, c: (acc.resumen?.fallidos ?? 0) > 0 ? 'text-amber-600' : '' },
                { l: t('pista_k_usuarios'), v: acc.resumen?.usuarios_distintos ?? 0, c: '' },
                { l: t('pista_k_ips'), v: acc.resumen?.ips_distintas ?? 0, c: '' },
              ].map((k) => (
                <div key={k.l} className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">{k.l}</div>
                  <div className={`text-xl font-semibold font-mono tabular-nums ${k.c}`}>{k.v}</div>
                </div>
              ))}
            </div>
          )}

          {/* La primera pregunta del auditor sobre gestión de accesos, respondida
              sin que nadie tenga que buscarla. */}
          {(acc?.nunca_entraron?.length ?? 0) > 0 && (
            <Card className="border-amber-500/40">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-600" />
                {t('pista_nunca', { n: acc!.nunca_entraron.length })}
              </CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('pista_nunca_nota')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {acc!.nunca_entraron.map((u) => (
                    <span key={u.username} className="rounded-full border px-2 py-0.5 text-xs"
                      title={t('pista_nivel', { perfil: u.perfil ?? t('pista_sin_perfil'), nivel: u.nivel ?? '—' })}>
                      {u.username}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="w-4 h-4" /> {t('pista_accesos_card', { n: acc?.accesos?.length ?? 0 })}
            </CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {(acc?.accesos?.length ?? 0) === 0 && !accesos.isFetching && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {t('pista_sin_accesos')}
                </p>
              )}
              {(acc?.accesos?.length ?? 0) > 0 && (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">{t('pista_c_cuando')}</th><th className="py-2 pr-3">{t('pista_c_usuario')}</th>
                    <th className="py-2 pr-3">{t('pista_c_resultado')}</th><th className="py-2 pr-3">{t('pista_c_ip')}</th>
                    <th className="py-2 pr-3">{t('pista_c_nav')}</th>
                  </tr></thead>
                  <tbody>
                    {acc!.accesos.map((a, i) => (
                      <tr key={i} className={`border-b last:border-0 ${a.resultado !== 'ok' ? 'bg-amber-500/5' : ''}`}>
                        <td className="py-2 pr-3 whitespace-nowrap text-xs">
                          {new Date(a.fecha).toLocaleString(LOCALE)}
                        </td>
                        <td className="py-2 pr-3">{a.usuario ?? a.username ?? '—'}</td>
                        <td className="py-2 pr-3">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            a.resultado === 'ok' ? 'bg-green-500/10 text-green-600' : 'bg-amber-500/10 text-amber-600'}`}>
                            {a.resultado === 'ok' ? t('pista_entro') : t('pista_rechazado')}
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs">{a.ip ?? '—'}</td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground max-w-xs truncate"
                          title={a.user_agent ?? ''}>{a.user_agent ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                {t('pista_pie_a')}<b>{t('pista_pie_b')}</b>{t('pista_pie_c')}
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
